import { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router';
import browserHistory from '~/browserHistory';

/**
 * Keeps the URL's candidate in sync with the candidate list:
 * - replaces the URL with the first visible candidate when the routed candidate
 *   is no longer in the list (but not when arriving via the back button), and
 * - reloads the candidate list exactly once per navigation entry when landing
 *   from a Decline action (keyed on location.key to avoid invalidation loops).
 */
export const useCandidateUrlSync = ({
  jobId,
  candidateId,
  allCandidates,
  visibleCandidates,
  isSuccessFetchJobCandidates,
  reloadCandidates,
  showCandidatePage,
}) => {
  const params = useParams();
  const location = useLocation();
  const declineReloadKeyRef = useRef(null);

  useEffect(() => {
    const hasRoutedCandidateInJob = allCandidates?.some(
      (counter) => counter?.candidate?.id === candidateId,
    );
    const hasRoutedCandidateVisible = visibleCandidates?.some(
      (counter) => counter?.candidate?.id === candidateId,
    );

    if (
      jobId &&
      candidateId &&
      isSuccessFetchJobCandidates &&
      hasRoutedCandidateInJob &&
      !hasRoutedCandidateVisible
    ) {
      showCandidatePage(candidateId);
      return;
    }

    if (
      jobId &&
      isSuccessFetchJobCandidates &&
      visibleCandidates?.length &&
      !hasRoutedCandidateInJob
    ) {
      browserHistory.replace(
        `/dashboard/job/${jobId}/confirm/${visibleCandidates[0].candidate.id}`,
      );
    }
  }, [
    allCandidates,
    candidateId,
    isSuccessFetchJobCandidates,
    jobId,
    showCandidatePage,
    visibleCandidates,
  ]);

  useEffect(() => {
    // Reload candidates list when coming back from DeclineCandidate action.
    const { jobId: routeJobId } = params;
    const fromDeclineCandidate = location.state?.fromDeclineCandidate;

    // Fire once per navigation entry. location.key is unique per history entry,
    // so a subsequent decline still reloads exactly once without looping invalidations.
    if (routeJobId && fromDeclineCandidate && declineReloadKeyRef.current !== location.key) {
      declineReloadKeyRef.current = location.key;
      reloadCandidates(routeJobId);
    }
  }, [params, location.state, location.key, reloadCandidates]);
};
